/**
 * Mount de la galería `/debriefings.html` — Fase 7g.2.
 *
 * Espejo de `briefings-list/mount.ts` con dos diferencias:
 *  - Llama a `listDebriefings()` (API más estricta: solo public ∪
 *    propios; admin sin atajo).
 *  - Click en fila navega a `/debriefing/ficha.html?id=<id>` (vista
 *    ficha read-only). El botón EDITAR de la ficha salta al editor
 *    `/debriefing.html?id=<id>` solo si el user es autor.
 *  - El botón X de borrado solo aparece para autor + superadmin
 *    (ver `canDeleteDebriefing`).
 *
 * Reutiliza la CSS de la galería de briefings (`bgallery-*` classes)
 * para mantener una estética unificada.
 */

import type { AuthenticatedPilot, MissionSummary } from '@51esc/shared';
import { mountChrome } from '../chrome/index.js';
import { openConfirmModal } from '../confirm-modal/index.js';
import {
  fetchBriefingMissions,
  fetchBriefingTargets,
  type BriefingMission,
} from '../briefing/api.js';
import { deleteDebriefing, listDebriefings } from '../debriefing/api.js';
import {
  renderEmptyRow,
  renderErrorRow,
  renderGalleryShell,
  renderRow,
} from './markup.js';

import '../briefings-list/briefings-list.css';

export async function mountDebriefingsList(): Promise<void> {
  const { contentRoot, user } = await mountChrome({
    activeSection: 'debriefings',
    allowAnonymous: true,
  });
  contentRoot.innerHTML = renderGalleryShell(user);

  const tbody = contentRoot.querySelector<HTMLElement>('[data-bgallery-rows]');
  if (!tbody) return;

  await reload(tbody, user);

  tbody.addEventListener('click', (ev) => {
    const target = ev.target as HTMLElement;
    const deleteBtn = target.closest<HTMLElement>('[data-bgallery-delete]');
    if (deleteBtn) {
      ev.stopPropagation();
      const id = deleteBtn.dataset['bgalleryDelete'];
      if (id) void onDeleteClick(id, tbody, user);
      return;
    }
    const tr = target.closest<HTMLElement>('[data-debriefing-id]');
    if (!tr) return;
    navigateToView(tr.dataset['debriefingId']);
  });
  tbody.addEventListener('keydown', (ev) => {
    if (ev.key !== 'Enter' && ev.key !== ' ') return;
    const tr = (ev.target as HTMLElement).closest<HTMLElement>(
      '[data-debriefing-id]',
    );
    if (!tr) return;
    ev.preventDefault();
    navigateToView(tr.dataset['debriefingId']);
  });
}

async function reload(
  tbody: HTMLElement,
  user: AuthenticatedPilot | null,
): Promise<void> {
  tbody.innerHTML = `<tr><td colspan="7" class="bgallery-loading">cargando…</td></tr>`;
  const [debriefingsRes, legacyMissions, targets] = await Promise.all([
    listDebriefings(),
    fetchBriefingMissions(),
    fetchBriefingTargets(),
  ]);

  const missionsCache = new Map<string, BriefingMission>();
  for (const m of legacyMissions) missionsCache.set(m.id, m);
  const declaredMissionsCache = new Map<string, MissionSummary>();
  for (const m of targets.missions) declaredMissionsCache.set(m.id, m);

  if (!debriefingsRes.ok) {
    tbody.innerHTML = renderErrorRow(debriefingsRes.message);
    return;
  }

  const list = debriefingsRes.data
    .slice()
    .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));

  if (list.length === 0) {
    tbody.innerHTML = renderEmptyRow(user);
    return;
  }

  tbody.innerHTML = list
    .map((d) =>
      renderRow(d, missionsCache, declaredMissionsCache, user),
    )
    .join('');
}

async function onDeleteClick(
  id: string,
  tbody: HTMLElement,
  user: AuthenticatedPilot | null,
): Promise<void> {
  // Defensa: el botón X no se renderiza para anónimos, pero si llegara
  // aquí sin sesión cortamos — el borrado es solo para usuarios logueados.
  if (user === null) return;
  const confirmed = await openConfirmModal({
    title: 'Borrar debriefing',
    message:
      `¿Seguro que quieres borrar este debriefing? Se eliminarán también ` +
      `sus fotos. Esta acción no se puede deshacer.`,
    confirmLabel: 'Borrar',
    danger: true,
  });
  if (!confirmed) return;

  const res = await deleteDebriefing(id);
  if (!res.ok) {
    await openConfirmModal({
      title: 'Error al borrar',
      message: res.message,
      confirmLabel: 'Cerrar',
      cancelLabel: 'Cerrar',
    });
    return;
  }
  await reload(tbody, user);
}

function navigateToView(id: string | undefined): void {
  if (!id) return;
  window.location.href = `/debriefing/ficha.html?id=${encodeURIComponent(id)}`;
}
